/* eslint-disable id-length -- the rule was disabled before a comment became required */
import {
  createContext,
  type FC,
  type PointerEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

const TWO = 2

/**
 * x: px from left to pointer pointer
 * y: px from top to pointer pointer
 */
const calculatePointerCoordsOnWrapper = (
  element: Element & EventTarget,
  event: PointerEvent,
): { x: number; y: number } => {
  const rect = element.getBoundingClientRect()
  const x = event.clientX - rect.left
  const y = event.clientY - rect.top

  return { x, y }
}

/**
 * adjust coords to sizes of zoomable image (image in gallery) and zoomed image.
 * example:
 * - image in gallery is 100x100px
 * - zoomed image is 200x200px
 * - pointer pointer is at left 50px top 50px
 * - adjustedX of initial left is 100px because target image is 2x bigger
 * - adjustedY of initial top is 100px because target image is 2x bigger
 *
 * Please note that this method assumes that the zoomable image is boxed
 * inside a square (aspect ratio 1:1) container with the sides equal to the
 * largest size of the image
 */
const adjustCoordsProportionalToZoomedImageSize = (
  zoomableImage: HTMLImageElement,
  zoomedImage: HTMLImageElement,
  { x, y }: { x: number; y: number },
): {
  adjustedX: number
  adjustedY: number
} => {
  const zoomableImageRect = zoomableImage.getBoundingClientRect()
  const maxSize = Math.max(zoomedImage.offsetWidth, zoomedImage.offsetHeight)
  const paddingX = (maxSize - zoomedImage.offsetWidth) / TWO
  const paddingY = (maxSize - zoomedImage.offsetHeight) / TWO

  const adjustedX = (maxSize / zoomableImageRect.width) * x - paddingX
  const adjustedY = (maxSize / zoomableImageRect.height) * y - paddingY

  return { adjustedX, adjustedY }
}

/**
 * translated values
 * example:
 * wrapper of zoomable image is 100x100px
 * pointer on wrapper of zoomable image is at left 50px
 * we want zoomed image to show at 50% from left.
 * zoomed image is 200x200px
 * adjustedX 50px on zoomable wrapper is 100px on zoomed because it is 2x bigger
 * zoomed image wrapper is 100x100px
 * we calculate 50% of wrapper = 50px
 * and subtract from adjusted 100 - 50 and get 50px - we need to translate x 50px
 * same is for top|y
 */
const calculateTranslateValues = (
  adjustedX: number,
  zoomedImageWrapper: HTMLElement,
  adjustedY: number,
): { translateX: number; translateY: number } => {
  const translateX = adjustedX - zoomedImageWrapper.offsetWidth / TWO
  const translateY = adjustedY - zoomedImageWrapper.offsetHeight / TWO

  return { translateX, translateY }
}

const IMG_LOWERCASE_NODE_NAME = 'img'

const getImageAndContainerFromEventTarget = (
  event: PointerEvent,
): { zoomableImage: HTMLImageElement; zoomableImageWrapper: HTMLElement } => {
  const targetElement: HTMLElement = event.target as HTMLElement

  const zoomableImageWrapper =
    targetElement.nodeName.toLowerCase() === IMG_LOWERCASE_NODE_NAME
      ? targetElement.parentElement
      : targetElement

  if (!zoomableImageWrapper) {
    throw new Error('Cannot determine zoomable image wrapper element')
  }

  const zoomableImage =
    targetElement.nodeName.toLowerCase() === IMG_LOWERCASE_NODE_NAME
      ? (targetElement as HTMLImageElement)
      : targetElement.querySelectorAll(IMG_LOWERCASE_NODE_NAME).item(0)

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- the rule was disabled before a comment became required
  if (!zoomableImage) {
    throw new Error('Cannot determine zoomable image element')
  }

  return { zoomableImage, zoomableImageWrapper }
}

const getTranslatedValues = ({
  event,
  zoomableImage,
  zoomableImageWrapper,
  zoomedImage,
  zoomedImageWrapper,
}: {
  event: PointerEvent
  zoomableImage: HTMLImageElement
  zoomableImageWrapper: HTMLElement
  zoomedImage: HTMLImageElement
  zoomedImageWrapper: HTMLElement
}): { translateX: number; translateY: number } => {
  const { x: xOnZoomableWrapper, y: yOnZoomableWrapper } =
    calculatePointerCoordsOnWrapper(zoomableImageWrapper, event)

  const { adjustedX, adjustedY } = adjustCoordsProportionalToZoomedImageSize(
    zoomableImage,
    zoomedImage,
    {
      x: xOnZoomableWrapper,
      y: yOnZoomableWrapper,
    },
  )

  return calculateTranslateValues(adjustedX, zoomedImageWrapper, adjustedY)
}

type ProductPageCarouselZoomableImageContextValue = {
  handleDisableZoomOnImageWrapperPointerLeave: () => void
  handleEnableZoomOnImageWrapperPointerEnter: (
    zoomedImageSrcToSet: string,
  ) => void
  handleMoveZoomOnImageWrapperPointerMove: (event: PointerEvent) => void
  translateValues: { translateX: number; translateY: number }
  zoomedImageRef: RefObject<HTMLImageElement | null>
  zoomedImageSrc?: string
  zoomedImageWrapperRef: RefObject<HTMLDivElement | null>
  zoomingIsActive: boolean
}

const ProductPageCarouselZoomableImageContext =
  createContext<ProductPageCarouselZoomableImageContextValue | null>(null)

export const ProductPageCarouselZoomableImageProvider: FC<{
  children: ReactNode
}> = ({ children }) => {
  const [translateValues, setTranslateValues] = useState({
    translateX: 0,
    translateY: 0,
  })

  const [zoomedImageSrc, setZoomedImageSrc] = useState<string | undefined>(
    undefined,
  )

  const zoomedImageRef = useRef<HTMLImageElement>(null)
  const zoomedImageWrapperRef = useRef<HTMLDivElement>(null)

  const handleMoveZoomOnImageWrapperPointerMove = useCallback(
    (event: PointerEvent): void => {
      const { zoomableImage, zoomableImageWrapper } =
        getImageAndContainerFromEventTarget(event)

      const zoomedImage = zoomedImageRef.current
      const zoomedImageWrapper = zoomedImageWrapperRef.current

      if (!zoomedImage || !zoomedImageWrapper) {
        return
      }

      const { translateX, translateY } = getTranslatedValues({
        event,
        zoomableImage,
        zoomableImageWrapper,
        zoomedImage,
        zoomedImageWrapper,
      })

      setTranslateValues({ translateX, translateY })
    },
    [],
  )

  const handleEnableZoomOnImageWrapperPointerEnter = useCallback(
    (zoomedImageSrcToSet: string): void => {
      setZoomedImageSrc(zoomedImageSrcToSet)
    },
    [],
  )

  const handleDisableZoomOnImageWrapperPointerLeave = useCallback((): void => {
    setZoomedImageSrc(undefined)
    setTranslateValues({ translateX: 0, translateY: 0 })
  }, [])

  const contextValue =
    useMemo((): ProductPageCarouselZoomableImageContextValue => {
      return {
        /**
         * when pointer leaves wrapper of image being zoomed, reset translate values,
         * reset zoomed image src and disable zooming
         */
        handleDisableZoomOnImageWrapperPointerLeave,
        /**
         * when pointer enters wrapper of image being zoomed, set zoomed image src and enable zooming
         */
        handleEnableZoomOnImageWrapperPointerEnter,
        /**
         * whenever pointer moves over wrapper of image being zoomed, recalculates translate value of the
         * zoomed image and sets it on state
         */
        handleMoveZoomOnImageWrapperPointerMove,
        /**
         * translate values of the zoomed image relative to it's wrapper
         */
        translateValues,
        zoomedImageRef,
        /**
         * src of the zoomed version of an image
         */
        zoomedImageSrc,
        zoomedImageWrapperRef,
        zoomingIsActive: Boolean(zoomedImageSrc),
      }
    }, [
      handleDisableZoomOnImageWrapperPointerLeave,
      handleEnableZoomOnImageWrapperPointerEnter,
      handleMoveZoomOnImageWrapperPointerMove,
      translateValues,
      zoomedImageSrc,
    ])

  return (
    <ProductPageCarouselZoomableImageContext.Provider value={contextValue}>
      {children}
    </ProductPageCarouselZoomableImageContext.Provider>
  )
}

export const useProductPageCarouselZoomableImageContext =
  (): ProductPageCarouselZoomableImageContextValue => {
    const context = useContext(ProductPageCarouselZoomableImageContext)

    if (!context) {
      throw new Error(
        'useProductPageCarouselZoomableImageContext_is_not_initialized',
      )
    }

    return context
  }
